"use client";

import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * The `wide:` variant (globals.css) — where the detail pages leave the phone
 * layout: a desktop, or any landscape window from 40rem (iPhone Duo open, or
 * closed and rotated; a landscape phone).
 *
 * Keep in step with the `wide:` classes in `DETAIL_DESKTOP`: the hook decides
 * what mounts (a vaul drawer or an inline column), the classes decide where it
 * sits, and a page whose two answers disagree renders half of each.
 */
export const DESKTOP_MEDIA_QUERY =
  "(min-width: 1024px), (min-width: 640px) and (orientation: landscape)";

const subscribe = (onChange: () => void) => {
  const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
};

const getSnapshot = () => window.matchMedia(DESKTOP_MEDIA_QUERY).matches;

// The server cannot know the width, so it renders the phone tree. Every
// desktop-only surface that must not flash before hydration carries its own
// `wide:` guard; this only picks which components mount.
const getServerSnapshot = () => false;

export function useIsDesktop() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Whether the WINDOW has scrolled past `threshold` — for the desktop layouts,
 * where the page scrolls on the window rather than inside a drawer.
 * Always false while `enabled` is false, so a phone never attaches it.
 */
export function useWindowScrolledPast(threshold: number, enabled: boolean) {
  const [scrolledPast, setScrolledPast] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setScrolledPast(false);
      return;
    }
    const update = () => setScrolledPast(window.scrollY > threshold);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [enabled, threshold]);

  return enabled && scrolledPast;
}
